
import { BirdieGameGroup, BirdieRecord } from "@/types";

export interface GameStatus {
  totalUniqueBirdiedHoles: number;
  statusMessage: string;
  statusClass: string;
  currentUserHoles: number;
  remainingHoles: number;
}

/**
 * Calculate game status for a user within a given game
 * @param game - The birdie game group
 * @param birdieRecords - List of birdie records for this specific game
 * @param userId - The current user ID (defaults to first member if not provided)
 * @returns GameStatus object with status information
 */
export const getUserGameStatus = (
  game: BirdieGameGroup, 
  birdieRecords: BirdieRecord[],
  userId?: string
): GameStatus => {
  // Safety check for empty inputs
  if (!game || !game.members || game.members.length === 0) {
    return {
      totalUniqueBirdiedHoles: 0,
      statusMessage: "No data available",
      statusClass: "text-muted-foreground",
      currentUserHoles: 0,
      remainingHoles: 18
    };
  }
  
  // Filter records for this specific game
  const gameRecords = birdieRecords.filter(record => 
    record.gameIds && record.gameIds.includes(game.id)
  );
  
  // Determine the current user ID
  // If not provided explicitly, use the first member (legacy behavior)
  const currentUserId = userId || game.members[0].id;
  
  // Create a Map to efficiently track unique birdied holes per player
  const uniqueHolesByPlayer = new Map<string, Set<number>>();
  
  // Initialize the Map for all members first to handle edge case of members with no birdies
  game.members.forEach(member => {
    uniqueHolesByPlayer.set(member.id, new Set());
  });
  
  // Populate holes for each player using game-specific records
  gameRecords.forEach(birdie => {
    const playerHolesSet = uniqueHolesByPlayer.get(birdie.userId);
    if (playerHolesSet) {
      playerHolesSet.add(birdie.holeNumber);
    }
  });
  
  // Get the authenticated user's unique hole count
  const currentUserHolesSet = uniqueHolesByPlayer.get(currentUserId) || new Set();
  const currentUserHoles = currentUserHolesSet.size;
  
  // Find the best competitor
  let bestCompetitorCount = 0;
  let bestCompetitorId = '';
  
  // Find who has the most birdies (excluding current user)
  uniqueHolesByPlayer.forEach((holesSet, playerId) => {
    const count = holesSet.size;
    if (playerId !== currentUserId && count > bestCompetitorCount) {
      bestCompetitorCount = count;
      bestCompetitorId = playerId;
    }
  });
  
  // Determine standings
  const isUserLeading = currentUserHoles > bestCompetitorCount;
  const isTied = currentUserHoles === bestCompetitorCount && bestCompetitorCount > 0;
  
  // Count players with top score for multi-way tie detection
  const topScore = Math.max(...Array.from(uniqueHolesByPlayer.values()).map(set => set.size));
  let playersWithTopScore = 0;
  
  uniqueHolesByPlayer.forEach((holesSet) => {
    if (holesSet.size === topScore) {
      playersWithTopScore++;
    }
  });
  
  // Calculate total unique holes with birdies across all players in the game
  const allBirdiedHoles = new Set<number>();
  
  // Use game-specific records only
  gameRecords.forEach(birdie => {
    allBirdiedHoles.add(birdie.holeNumber);
  });
  
  const totalUniqueBirdiedHoles = allBirdiedHoles.size;
  const remainingHoles = Math.max(0, 18 - totalUniqueBirdiedHoles);
  
  // Edge case: If all players are tied at 0, don't show "You're tied for the lead"
  if (topScore === 0) {
    return {
      totalUniqueBirdiedHoles,
      statusMessage: "No holes birdied yet",
      statusClass: "text-muted-foreground",
      currentUserHoles,
      remainingHoles
    };
  }

  // Single-player games: show progress instead of competitive ranking
  if (game.members.length <= 1) {
    return {
      totalUniqueBirdiedHoles,
      statusMessage: `${currentUserHoles} of 18 holes birdied`,
      statusClass: "text-fairway font-semibold",
      currentUserHoles,
      remainingHoles
    };
  }

  // Determine status message based on the game state
  let statusMessage: string;
  let statusClass: string;
  
  if (isUserLeading) {
    // User is in the lead without a tie
    const diff = currentUserHoles - bestCompetitorCount;
    statusMessage = `You're ahead by ${diff} ${diff === 1 ? 'hole' : 'holes'}`;
    statusClass = "text-emerald-500 font-semibold";
  } else if (isTied) {
    // More specific tied message if more than 2 players are tied
    if (playersWithTopScore > 2) {
      statusMessage = `You're in a ${playersWithTopScore}-way tie`;
    } else {
      statusMessage = "You're tied for the lead";
    }
    statusClass = "text-amber-500 font-semibold";
  } else {
    // User is behind
    const diff = bestCompetitorCount - currentUserHoles;
    statusMessage = `You're behind by ${diff} ${diff === 1 ? 'hole' : 'holes'}`;
    statusClass = "text-blue-500 font-semibold";
  }
  
  return {
    totalUniqueBirdiedHoles,
    statusMessage,
    statusClass,
    currentUserHoles,
    remainingHoles
  };
};
